import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden grain"
      data-testid="hero-section"
    >
      {/* soft background shape */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle at 30% 30%, #E8D5C4 0%, transparent 60%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-40 w-[480px] h-[480px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle at 60% 60%, #D5E0D5 0%, transparent 65%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-sentivo-surface px-4 py-2 text-xs tracking-[0.18em] uppercase text-sentivo-secondary mb-8"
            data-testid="hero-eyebrow"
          >
            <Sparkles size={14} className="text-sentivo-accent" />
            AI for Human Wellness
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-sentivo-ink text-balance"
            data-testid="hero-headline"
          >
            Intelligence that
            <span className="italic text-sentivo-secondary"> cares </span>
            <br className="hidden sm:block" />
            for every stage of life.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-7 max-w-xl text-lg text-sentivo-muted leading-relaxed"
            data-testid="hero-subhead"
          >
            SentivoTech builds context‑aware AI that turns reactive caregiving into
            proactive wellness — across elderly care, longevity, autism support and
            cognitive health.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#elderlyassist"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-sentivo-accent hover:bg-sentivo-accentHover text-white px-7 py-4 text-sm font-semibold tracking-wide transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              Discover ElderlyAssist
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#focus"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-sentivo-line bg-white/40 backdrop-blur-sm hover:bg-white text-sentivo-primary px-7 py-4 text-sm font-semibold tracking-wide transition-all duration-300"
            >
              Our focus areas
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-md"
          >
            {[
              { k: "6+", v: "AI capabilities" },
              { k: "24/7", v: "Predictive care" },
              { k: "5", v: "Wellness verticals" },
            ].map((s) => (
              <div key={s.v} className="border-l border-sentivo-line pl-4">
                <div className="font-serif text-3xl text-sentivo-primary">{s.k}</div>
                <div className="text-xs uppercase tracking-widest text-sentivo-muted mt-1">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* RIGHT */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="lg:col-span-5 relative"
          data-testid="hero-image-block"
        >
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwc21pbGluZyUyMGNhcmVnaXZlcnxlbnwwfHx8fDE3NzkzNzM3MzB8MA&ixlib=rb-4.1.0&q=85"
              alt="A caregiver and elderly companion sharing a warm moment"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(44,62,53,0) 50%, rgba(44,62,53,0.55) 100%)",
              }}
            />
          </div>

          {/* floating card */}
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-6 -left-6 md:-left-10 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-5 max-w-[260px] border border-sentivo-line"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[11px] uppercase tracking-widest text-sentivo-muted">
                Live signal
              </span>
            </div>
            <div className="font-serif text-lg text-sentivo-ink leading-snug">
              "Gentle, predictive care that protects independence."
            </div>
          </motion.div>

          <div
            aria-hidden
            className="absolute -top-6 -right-6 w-28 h-28 rounded-full border border-sentivo-accent/40 hidden md:block"
          />
        </motion.div>
      </div>
    </section>
  );
};
